import { useLayoutMargins } from '@pbk-web/state';
import type { FCWithChildren } from '@pbk-web/types';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalContextConsumer, ModalContextProvider } from '../../../context/ModalContext';
import Button, { ButtonSize, ButtonType } from '../../buttons/Button';
import { Heading, HeadingSize } from '../../text/Heading';
import { Modal } from '../Modal';

type Props = {
  title: string;
  subTitle?: string;
  titleIcon?: ReactElement;
  onTitleIconClick?: () => void;
  onCancelClick: () => void;
  onConfirmClick: () => void;
  confirmDisabled?: boolean;
  padding?: number | string;
  bodyPadding?: string;
};

const FullscreenModal: FCWithChildren<Props> = (props) => {
  const {
    padding = '1.5rem',
    bodyPadding,
    children,
    title,
    subTitle,
    onCancelClick,
    confirmDisabled,
    onConfirmClick,
    titleIcon,
    onTitleIconClick = props.onCancelClick,
  } = props;
  const layoutMargins = useLayoutMargins((x) => x.value);

  const sizeToString = (size: number | string) => (typeof size === 'number' ? `${size}px` : size);

  const { t } = useTranslation('common');

  return (
    <ModalContextConsumer>
      {(value) => (
        <ModalContextProvider
          value={{
            ...value,
            modalWidth: 'w-full',
          }}
        >
          <Modal
            extraStyles={{
              margin: sizeToString(padding),
              marginLeft: `calc(${sizeToString(padding)} + ${sizeToString(layoutMargins.left)})`,
              marginTop: `calc(${sizeToString(padding)} + ${sizeToString(layoutMargins.top ?? 0)})`,
              maxWidth: `calc(100vw - (2 * ${sizeToString(padding)}) - ${sizeToString(layoutMargins.left)} - ${sizeToString(layoutMargins.right ?? 0)})`,
              minHeight: `calc(100vh - (2 * ${sizeToString(padding)}) - ${sizeToString(layoutMargins.top ?? 0)} - ${sizeToString(layoutMargins.bottom ?? 0)})`,
            }}
          >
            <Modal.Header align="left" underline={!!title || !!subTitle}>
              <div className="flex items-start gap-1">
                <div className="mt-1 -ml-3 flex cursor-pointer items-start empty:hidden" onClick={onTitleIconClick}>
                  {titleIcon}
                </div>
                <div>
                  {title && (
                    <Heading size={HeadingSize.H5} className="truncate font-medium">
                      {title}
                    </Heading>
                  )}
                  {subTitle && (
                    <>
                      {/* Tooltip seperator */}
                      <span className="hidden"> - </span>

                      <div className="text-pbk-16 text-text-4 py-1 font-normal">{subTitle}</div>
                    </>
                  )}
                </div>
              </div>
            </Modal.Header>

            <Modal.Body maxHeight="" padding={bodyPadding}>
              {children}
            </Modal.Body>

            <Modal.Footer justification="justify-end">
              <div className="flex gap-4">
                <Button type={ButtonType.SECONDARY} size={ButtonSize.S} onClick={onCancelClick} data-testid="modal-cancel">
                  {t('modals.cancel')}
                </Button>
                <Button type={ButtonType.PRIMARY} size={ButtonSize.S} disabled={confirmDisabled} onClick={onConfirmClick} data-testid="modal-done">
                  {t('modals.done')}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        </ModalContextProvider>
      )}
    </ModalContextConsumer>
  );
};

export default FullscreenModal;
